import { ChartDataItem } from '@/components/Chart/Chart'
import { ComparisonChartDataItem } from '@/components/Chart/ComparisonChart'
import { formatLargeNumber } from '@/utils/formatNumber'

const MAX_TICK_COUNT = 7
const DAYS_STEPS = [1 / 24, 1 / 6, 1 / 2, 1, 2, 3, 7, 14, 30, 60, 90, 180, 365, 365 * 2]

export const calculateTickInterval = (dataLength: number): number =>
  dataLength <= MAX_TICK_COUNT ? 0 : Math.ceil(dataLength / 7) - 1

export const getYAxisDomain = (
  data: ComparisonChartDataItem[],
  value: 'value1' | 'value2'
): [number, number] => {
  const values = data.map((d) => (value === 'value2' ? (d.value2 ?? 0) : d.value1))

  const min = Math.min(...values)
  const max = Math.max(...values)

  if (min < 0) {
    const maxAbs = Math.max(Math.abs(min), Math.abs(max))
    return [-maxAbs, maxAbs]
  }

  return [0, max]
}

export const calculateTickIndices = (data: { date: string }[]): number[] => {
  const dataLength = data.length
  if (dataLength === 0) return []

  const firstDate = new Date(data[0].date)
  const lastDate = new Date(data[dataLength - 1].date)
  const daysDiff = (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24)

  const idealStep = daysDiff / (MAX_TICK_COUNT - 1)

  const stepDays = DAYS_STEPS.reduce((prev, curr) =>
    Math.abs(curr - idealStep) < Math.abs(prev - idealStep) ? curr : prev
  )

  const stepMs = stepDays * 24 * 60 * 60 * 1000

  // step backwards from last date so the last point is always a tick
  const targetTimes: number[] = []
  let current = lastDate.getTime()
  while (current >= firstDate.getTime()) {
    targetTimes.push(current)
    current -= stepMs
  }

  // snap each target time to the nearest real data point
  const tickSet = new Set<number>()

  for (const targetTime of targetTimes) {
    let closestIndex = 0
    let closestDiff = Infinity

    data.forEach((item, i) => {
      const diff = Math.abs(new Date(item.date).getTime() - targetTime)
      if (diff < closestDiff) {
        closestDiff = diff
        closestIndex = i
      }
    })

    tickSet.add(closestIndex)
  }

  // last point always visible
  tickSet.add(dataLength - 1)

  let ticks = [...tickSet].sort((a, b) => a - b)

  // thin out if snapping produced too many ticks
  if (ticks.length > MAX_TICK_COUNT + 1) {
    const last = ticks[ticks.length - 1]
    const inner = ticks.slice(0, -1)
    const step = Math.ceil(inner.length / (MAX_TICK_COUNT - 1))
    ticks = [...inner.filter((_, i) => i % step === 0), last]
  }

  // dynamic minimum spacing based on average gap between ticks
  const avgGap =
    ticks.length > 1 ? Math.round((ticks[ticks.length - 1] - ticks[0]) / (ticks.length - 1)) : 1

  // drop ticks too close to their neighbour
  return ticks.filter((tick, i) => {
    if (i === ticks.length - 1) return true
    return ticks[i + 1] - tick >= avgGap
  })
}

export const createTickFormatter = (data: { name: string; date: string }[]) => {
  return (index: number) => {
    const item = data[index]
    if (!item) return ''

    return item.name
  }
}

export const getYAxisTicks = (
  data: (ComparisonChartDataItem | ChartDataItem)[],
  key: 'value1' | 'value2' | 'value',
  tickCount = 6
): { domain: [number, number]; ticks: number[] } => {
  const rawValues = data
    .map((d) => {
      switch (key) {
        case 'value2':
          return (d as ComparisonChartDataItem).value2
        case 'value1':
          return (d as ComparisonChartDataItem).value1
        case 'value':
        default:
          return (d as ChartDataItem).value
      }
    })
    .filter((v): v is number => v !== undefined && v !== null)

  if (!rawValues.length) {
    return { domain: [0, 1], ticks: [0, 1] }
  }

  const min = Math.min(...rawValues)
  const max = Math.max(...rawValues)

  const hasNegative = min < 0

  // handle negative symmetry
  const absMax = hasNegative ? Math.max(Math.abs(min), Math.abs(max)) : max
  const baseMin = hasNegative ? -absMax : 0
  const baseMax = absMax

  const range = baseMax - baseMin

  if (tickCount < 2) {
    tickCount = 2
  }

  if (range === 0) {
    const value = baseMax
    const step = value === 0 ? 1 : Math.abs(value) * 0.1

    const newMin = hasNegative ? value - step : 0
    const newMax = value === 0 ? step : value + step

    return {
      domain: [newMin, newMax],
      ticks: value === 0 ? [0, step] : [newMin, value, newMax],
    }
  }

  let step = range / (tickCount - 1)

  // normalization
  const magnitude = Math.pow(10, Math.floor(Math.log10(step)))
  const normalized = step / magnitude

  if (normalized <= 1) {
    step = 1 * magnitude
  } else if (normalized <= 2) {
    step = 2 * magnitude
  } else if (normalized <= 5) {
    step = 5 * magnitude
  } else {
    step = 10 * magnitude
  }

  const newMin = Math.floor(baseMin / step) * step
  const newMax = Math.ceil(baseMax / step) * step

  const ticks: number[] = []

  for (let v = newMin; v <= newMax; v += step) {
    ticks.push(Number(v.toFixed(10)))
  }

  return {
    domain: [newMin, newMax],
    ticks,
  }
}

export const formatYAxisTick = (value: number, ticks: number[]) => {
  const formatted = formatLargeNumber(value, 0)

  const hasDuplicate = ticks.some((t) => t !== value && formatLargeNumber(t, 0) === formatted)

  if (hasDuplicate) {
    return formatLargeNumber(value, 1)
  }

  return formatted
}
